import { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users2, Flame, UserCog } from "lucide-react";
import ContentTagPicker from "@/components/ContentTagPicker";

/** Categorie iniziali (allineate a Fencing.jsx per compat legacy). */
const CATEGORIES = [
  { v: "attivazione",      l: "Attivazione" },
  { v: "prep_fisica",      l: "Preparazione fisica" },
  { v: "gambe_scherma",    l: "Gambe-scherma" },
  { v: "tecnica",          l: "Tecnica" },
  { v: "tattica",          l: "Tattica" },
  { v: "coppie",           l: "Esercitazioni a coppie" },
  { v: "assalti_tema",     l: "Assalti a tema" },
  { v: "assalti_liberi",   l: "Assalti liberi" },
  { v: "circuito",         l: "Circuito" },
  { v: "recupero",         l: "Recupero" },
  { v: "altro",            l: "Altro" },
];
const DURATIONS = [10, 15, 20, 30, 45, 60];
const INTENSITIES = [1, 2, 3, 4, 5];

/**
 * QuickActivityDialog
 * Registrazione rapida di un'attività di gruppo.
 * Flow: apri → categoria → gruppo → durata → contenuti (opzionale) → tecnici → Salva.
 */
export default function QuickActivityDialog({ open, onOpenChange, sessionId, defaultCoachId, onSaved }) {
  const [groups, setGroups] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [leadCoachId, setLeadCoachId] = useState("");
  const [category, setCategory] = useState("tecnica");
  const [duration, setDuration] = useState(20);
  const [intensity, setIntensity] = useState(null);
  const [tags, setTags] = useState([]);
  const [extraCoaches, setExtraCoaches] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/groups"), api.get("/users")]).then(([g, u]) => {
      setGroups(g.data || []);
      setCoaches((u.data || []).filter((x) => ["coach", "admin", "trainer"].includes(x.role)));
    }).catch(() => {});
    setGroupId(""); setCategory("tecnica"); setDuration(20); setIntensity(null); setTags([]); setExtraCoaches([]);
    setLeadCoachId(defaultCoachId || "");
  }, [open, defaultCoachId]);

  const group = useMemo(() => groups.find((g) => g.id === groupId), [groups, groupId]);
  const weapon = group?.weapon || (group?.weapons || [])[0] || null;

  const toggleCoach = (id) => setExtraCoaches((v) => v.includes(id) ? v.filter((x) => x !== id) : [...v, id]);

  const save = useCallback(async () => {
    if (!groupId) { toast.error("Seleziona il gruppo"); return; }
    if (!leadCoachId) { toast.error("Seleziona il tecnico esecutore"); return; }
    setSaving(true);
    try {
      let sid = sessionId;
      if (!sid) {
        const { data } = await api.post("/training/ensure-session");
        sid = data.id;
      }
      const coachSet = new Set([leadCoachId, ...extraCoaches].filter(Boolean));
      const coach_ids = coachSet.size > 1 ? Array.from(coachSet) : null;
      const payload = {
        session_id: sid,
        type: "group_training",
        type_label: (CATEGORIES.find((c) => c.v === category) || {}).l || category,
        lead_coach_id: leadCoachId,
        coach_ids,
        group_ids: [groupId],
        duration_min: duration,
        content_tag_codes: tags,
        theme: (CATEGORIES.find((c) => c.v === category) || {}).l || category,
      };
      if (intensity != null) payload.intensity = intensity;
      const { data: activity } = await api.post("/activities", payload);
      try { await api.post(`/activities/${activity.id}/complete`); } catch {}
      toast.success(`Attività salvata · ${duration}′`);
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio attività");
    } finally {
      setSaving(false);
    }
  }, [groupId, leadCoachId, sessionId, extraCoaches, category, duration, intensity, tags, onOpenChange, onSaved]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quick-activity-dialog" className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users2 className="w-5 h-5" />Nuova attività di gruppo</DialogTitle>
          <DialogDescription>Registra un'attività di gruppo con categoria, gruppo e contenuti.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="flex items-center gap-1"><UserCog className="w-3.5 h-3.5" />Tecnico esecutore *</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {coaches.map((c) => (
                <button
                  key={c.id}
                  data-testid={`qa-lead-${c.id}`}
                  onClick={() => { setLeadCoachId(c.id); setExtraCoaches((v) => v.filter((x) => x !== c.id)); }}
                  className={`h-10 px-3 rounded-full text-sm border ${
                    leadCoachId === c.id ? "bg-black text-white border-black" : "bg-white border-zinc-300"
                  }`}
                >{c.name}</button>
              ))}
            </div>
          </div>

          <div>
            <Label>Categoria</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {CATEGORIES.map((c) => (
                <button
                  key={c.v}
                  data-testid={`qa-cat-${c.v}`}
                  onClick={() => setCategory(c.v)}
                  className={`h-10 px-3 rounded-md border text-sm ${
                    category === c.v ? "bg-black text-white border-black" : "bg-white border-zinc-300"
                  }`}
                >{c.l}</button>
              ))}
            </div>
          </div>

          <div>
            <Label>Gruppo</Label>
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger data-testid="qa-group" className="h-12">
                <SelectValue placeholder="Seleziona gruppo" />
              </SelectTrigger>
              <SelectContent>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.name}{g.weapon ? ` · ${g.weapon}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Durata (min)</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {DURATIONS.map((d) => (
                <button
                  key={d}
                  data-testid={`qa-dur-${d}`}
                  onClick={() => setDuration(d)}
                  className={`h-11 px-4 rounded-md border text-sm ${
                    duration === d ? "bg-black text-white border-black" : "bg-white border-zinc-300"
                  }`}
                >{d}′</button>
              ))}
            </div>
          </div>

          <div>
            <Label>Contenuti (opzionale)</Label>
            <ContentTagPicker value={tags} onChange={setTags} weapon={weapon} maxHeight="150px" />
          </div>

          <div>
            <Label className="flex items-center gap-1"><Flame className="w-3.5 h-3.5" />Intensità (facoltativa)</Label>
            <div className="flex gap-2 mt-1">
              {INTENSITIES.map((i) => (
                <button
                  key={i}
                  data-testid={`qa-int-${i}`}
                  onClick={() => setIntensity(intensity === i ? null : i)}
                  className={`h-11 w-11 rounded-md border text-sm ${
                    intensity === i ? "bg-black text-white border-black" : "bg-white border-zinc-300"
                  }`}
                >{i}</button>
              ))}
            </div>
          </div>

          {coaches.length > 1 && (
            <div>
              <Label>Co-tecnici (facoltativo)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {coaches.filter((c) => c.id !== leadCoachId).map((c) => (
                  <button
                    key={c.id}
                    data-testid={`qa-coach-${c.id}`}
                    onClick={() => toggleCoach(c.id)}
                    className={`h-10 px-3 rounded-full text-sm border ${
                      extraCoaches.includes(c.id) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300"
                    }`}
                  >{c.name}</button>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button
            data-testid="qa-save"
            onClick={save}
            disabled={saving || !groupId}
            className="bg-black hover:bg-zinc-800 h-11 px-6"
          >
            {saving ? "Salvo…" : "Salva attività"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
